// ─────────────────────────────────────────────────────────────────────────────
// Offers content — single source of copy for the shared OffersClient shell.
// Two variants share identical layout/pricing; only the copy differs, so an
// A/B test (generic /offers vs niched /contractors) stays apples-to-apples.
// Prices are identical across variants — never split-test price and copy at once.
// ─────────────────────────────────────────────────────────────────────────────

export type Currency = "GBP" | "ZAR"

export type Package = {
  number: string
  name: string
  forWho: string
  tagline: string
  description: string
  badge: string
  price: Record<Currency, number>
  setup: Record<Currency, number>
  period: string
  features: string[]
  featured: boolean
  /** One plain sentence shown on the card (see GUARANTEE). */
  guarantee: string
  /** Full Pipeline: the monthly fee starts on launch day, not with the setup fee. */
  monthlyStartsAtLaunch?: boolean
  // Per-currency value anchor rendered under the price — states what the
  // package would cost bought separately, so the price reads as value, not cost.
  anchorNote?: Record<Currency, string>
  // Optional launch offer — only rendered when the active currency matches (e.g. a ZAR-only SA special).
  // None is running right now: the ZAR Opening Special was retired 2026-08-24. Set this to bring one back.
  openingSpecial?: { currency: Currency; label: string; badge: string; upfront: number; monthly: number; months: number; regularNote: string }
}

// The third, unpriced card: scoped custom work. Floor shown only where a
// currency has a researched one (ZAR R50,000, 2026-11-02); otherwise "quoted per project".
export type CustomPackage = {
  number: string
  name: string
  forWho: string
  tagline: string
  description: string
  badge: string
  floor: Partial<Record<Currency, number>>
  note: string
  features: string[]
  cta: string
  guarantee: string
}

/** The four promises, shown once under the package cards and in full on /refund. */
export type Guarantees = {
  eyebrow: string
  heading: string
  intro: string
  items: { title: string; body: string }[]
  footnote: string
}

export type OffersContent = {
  variant: string // used to tag CTA links (/contact?variant=…) for conversion attribution
  eyebrow: string
  heroTitle: [string, string] // [plain line, accent line]
  heroSubhead: string
  packages: Package[]
  faqs: { q: string; a: string }[]
  custom: { heading: string; body: string }
  customPackage: CustomPackage
  guarantees: Guarantees
  cta: { title: [string, string]; subhead: string; primary: string }
}

// Pricing is shared so it never diverges between variants.
const PRICE = {
  chatbot: { GBP: 297, ZAR: 2950 } as Record<Currency, number>,
  holistic: { GBP: 479, ZAR: 5950 } as Record<Currency, number>,
}

// Value anchors: what the package would cost bought separately. ZAR figures are
// market-sourced (SA custom builds R9k–R15k once-off; SA GHL implementers quote
// the separate-tools stack at R5,000–R15,000/mo). Keep claims conservative.
const ANCHOR = {
  chatbot: {
    ZAR: "SA agencies charge R7,500–R20,000 for a custom site alone. Yours comes with the AI assistant, and we keep both running.",
    GBP: "A custom build like this runs £2,000+ elsewhere — here it's included.",
  } as Record<Currency, string>,
  holistic: {
    ZAR: "Replaces R5,000–R15,000/mo in separate tools — one system, one price.",
    GBP: "Replaces £300–£600/mo in separate tools — one system, one price.",
  } as Record<Currency, string>,
}

// One-time setup fee per package — charged in full on monthly plans, HALF on annual
// (see annualSetup). Repriced 2026-11-02: ZAR setups R9,997 / R19,997.
const SETUP = {
  chatbot: { GBP: 597, ZAR: 9997 } as Record<Currency, number>,
  holistic: { GBP: 1197, ZAR: 19997 } as Record<Currency, number>,
}

// ── Guarantees (2026-11-04) ─────────────────────────────────────────────────
// One sentence per package on the cards, the four promises under the cards, and
// the full conditions on /refund (refund/legal-content.ts). All three must say the
// same thing: change one, change the others. The launch date applies to Website +
// AI Chat only; Full Pipeline and Custom Systems have no time limit, and Full
// Pipeline's monthly fee starts on launch day (estimate.ts + ghl-invoice.ts bill it so).
export const GUARANTEE = {
  chatbot: "Our guarantee: love your design or get every cent back, and your site is live in 2 weeks or your next month is free.",
  holistic: "Our guarantee: love your design or get every cent back, and you don't pay a monthly fee until your system is live.",
  custom: "Our guarantee: love your design or get every cent back, and the price we agree on your scoping call includes everything, from design to launch.",
}

export const GUARANTEES: Guarantees = {
  eyebrow: "Guarantees",
  heading: "Four promises we put in writing",
  intro: "We take the risk off you. Here's exactly what we promise, in plain words.",
  items: [
    { title: "Love your design, or get every cent back.", body: "You see your homepage design before we build anything. If you don't love it after 2 rounds of changes, we refund you in full." },
    { title: "Live on time, or your next month is free.", body: "On Website + AI Chat, your site goes live 2 weeks after you send us your text, photos and logo. If we're late, your next month is free." },
    { title: "Fast and working on every phone, or we fix it free.", body: "Every page scores 80 or more on Google's own website test, and works on phones, tablets and laptops. If a page falls short at launch, we fix it at no cost." },
    { title: "Leave any month.", body: "Cancel with one email. There's no contract to break and no exit fee." },
  ],
  footnote: "The fine print is short, and you can read all of it in our refund policy.",
}

/** Annual plans pay half the one-time setup fee (rounded to the rand/pound). */
export function annualSetup(setup: number): number {
  return Math.round(setup / 2)
}

// Floor for the Custom Systems card (2026-11-02). ZAR: bottom of SA custom-build pricing.
// GBP: bottom of UK agency custom site/ecommerce pricing (£5k–£15k; web apps £15k+).
const CUSTOM_FLOOR: Partial<Record<Currency, number>> = { ZAR: 50000, GBP: 5000 }

// FAQ answers shared by both variants (and mirrored in home-content.ts and the CMS).
export const FAQ_MONTHLY = "There's a one-time setup fee to build your system, then a monthly plan that keeps it running. On Website + AI Chat, your first month is paid with the setup fee. On the Full Pipeline, your monthly fee only starts on the day your system goes live. Pay annually and you pay half the setup fee. Prefer a pure once-off build? Mention it on the call and we'll quote it."
export const FAQ_TERM = "No. Email us any time to cancel, with no penalty and no notice needed. Monthly plans stop at the end of the month you've paid for. Annual plans give you 12 months for the price of 10, paid upfront, with half the setup fee. Cancel an annual plan and we refund the months you haven't used, after charging the months you did use at the monthly price."
export const FAQ_FIT = "You see your homepage design before we build anything. If you don't love it after 2 rounds of changes, tell us within 14 days and you get every cent back. Once you approve the design, we start building, and the setup fee pays for that work. The full details are in our refund policy."

// ── VARIANT A: generic (businesses) — lives at /offers ───────────────────────
export const genericContent: OffersContent = {
  variant: "offers",
  eyebrow: "Offers",
  heroTitle: ["Pick your", "starting point."],
  heroSubhead:
    "From a premium website to a fully automated system — all on one platform that captures leads, books calls, and follows up for you. Start where you are; level up when you're ready.",
  packages: [
    {
      number: "01",
      name: "Website + AI Chat",
      forWho: "For businesses losing leads after hours.",
      tagline: "A site that sells while you sleep.",
      description:
        "A custom site that makes you the obvious choice, plus an AI assistant trained on your business — answering questions and catching leads at 2am, so none slip through while you're busy.",
      badge: "Most Popular",
      price: PRICE.chatbot,
      setup: SETUP.chatbot,
      anchorNote: ANCHOR.chatbot,
      period: "/mo",
      features: [
        "Custom-designed website in your brand — no templates",
        "Built to be found on Google and AI search",
        "AI chatbot trained on your business",
        "Answers questions and captures leads 24/7",
        "Every enquiry lands in your CRM instantly",
        "Priority support",
      ],
      featured: false,
      guarantee: GUARANTEE.chatbot,
    },
    {
      number: "02",
      name: "Full Pipeline",
      forWho: "For businesses done duct-taping tools together.",
      tagline: "Your whole business, on one platform.",
      description:
        "The complete system — website, chatbot, CRM, bookings, and follow-up, all connected from day one. Leads come in, get nurtured, and book themselves in without you lifting a finger.",
      badge: "Complete System",
      price: PRICE.holistic,
      setup: SETUP.holistic,
      anchorNote: ANCHOR.holistic,
      period: "/mo",
      features: [
        "Everything in Website + AI Chat",
        "Full CRM and sales pipeline, set up for you",
        "Automated email and SMS follow-up",
        "Online booking wired to your calendar",
        "Lead-nurture sequences that close the gap",
        "A monthly report on what's working",
      ],
      featured: true,
      guarantee: GUARANTEE.holistic,
      monthlyStartsAtLaunch: true,
    },
  ],
  faqs: [
    { q: "Why are prices shown in my currency?", a: "We detect your region and show prices in your local currency automatically. You can switch between GBP and ZAR anytime using the toggle above — nothing is locked." },
    { q: "Is this monthly or a once-off cost?", a: FAQ_MONTHLY },
    { q: "Is there a minimum term?", a: FAQ_TERM },
    { q: "What if it's not the right fit?", a: FAQ_FIT },
    { q: "Can I start small and upgrade later?", a: "Yes. Start with Website + AI Chat and move up to the Full Pipeline whenever you're ready. Everything you build carries forward — no rework, no restart." },
    { q: "Do I own my website and brand assets?", a: "Your text, photos, logo and customer details are yours. On a monthly or annual plan, the site runs on our code and our hosting while you're subscribed. Cancel, and it goes offline at the end of your last paid month. Email us within 30 days and we'll send you your text, photos and logo. Want the code outright? Ask us for a once-off build quote on the call." },
  ],
  custom: {
    heading: "Need something bigger?",
    body: "Multi-location, deeper integrations, a fully custom build, or a system the packages don't cover? We'll scope it with you and quote it properly.",
  },
  customPackage: {
    number: "03",
    name: "Custom Systems",
    forWho: "For businesses that have outgrown a package.",
    tagline: "Built around how you already work.",
    description:
      "Online stores, course platforms, several branches, or software built for one job. Tell us what has to happen, and we'll scope it, price it and build it with a senior developer.",
    badge: "Quoted by scope",
    floor: CUSTOM_FLOOR,
    note: "Monthly care plan priced alongside the build",
    features: [
      "Online stores and course platforms",
      "Custom software and integrations",
      "Setups for several branches or teams",
      "A fixed quote after one scoping call",
      "A monthly care plan priced with the build",
    ],
    cta: "Book a Scoping Call",
    guarantee: GUARANTEE.custom,
  },
  guarantees: GUARANTEES,
  cta: {
    title: ["Ready to", "get started?"],
    subhead: "Tell us about your business. We'll come back with a clear plan — no fluff, no hard sell.",
    primary: "Start a Project",
  },
}

// ── VARIANT B: contractors (niched) — lives at /contractors ──────────────────
export const contractorContent: OffersContent = {
  variant: "contractors",
  eyebrow: "For contractors",
  heroTitle: ["Win the jobs", "you're missing."],
  heroSubhead:
    "Every missed call on site, every quote that went cold, every lead that called the next guy while you were on the tools — that's work walking out the door. We build the website and system that catches it, so the job comes to you.",
  packages: [
    {
      number: "01",
      name: "Website + AI Chat",
      forWho: "For contractors who can't answer the phone on a roof.",
      tagline: "It answers while you're on the job.",
      description:
        "A website that makes you the obvious call, plus an AI assistant trained on your trade — answering questions and booking the job at 7am, at 10pm, while you're up a ladder. The lead never waits, and never calls the next guy.",
      badge: "Most Popular",
      price: PRICE.chatbot,
      setup: SETUP.chatbot,
      anchorNote: ANCHOR.chatbot,
      period: "/mo",
      features: [
        "Custom site showing your work and reviews",
        "Found on Google for your trade and town",
        "AI assistant trained on your trade and pricing",
        "Answers questions and books jobs 24/7",
        "Catches the after-hours leads you'd miss",
        "Every lead lands in your CRM instantly",
      ],
      featured: false,
      guarantee: GUARANTEE.chatbot,
    },
    {
      number: "02",
      name: "Full Pipeline",
      forWho: "For contractors done losing jobs to admin.",
      tagline: "Your whole job pipeline, handled.",
      description:
        "The complete system — website, AI assistant, CRM, quote follow-up, and booking, all connected. Leads come in, get chased, and book themselves onto your calendar while you stay on the tools. No lead slips, no admin nights.",
      badge: "Complete System",
      price: PRICE.holistic,
      setup: SETUP.holistic,
      anchorNote: ANCHOR.holistic,
      period: "/mo",
      features: [
        "Everything in Website + AI Chat",
        "Full CRM and job pipeline, set up for you",
        "Automatic quote follow-up by text and email",
        "Online booking wired to your calendar",
        "Win-back sequences for cold quotes",
        "A monthly report on jobs won",
      ],
      featured: true,
      guarantee: GUARANTEE.holistic,
      monthlyStartsAtLaunch: true,
    },
  ],
  faqs: [
    { q: "Why are prices shown in my currency?", a: "We detect your region and show prices in your local currency automatically. You can switch between GBP and ZAR anytime using the toggle above — nothing is locked." },
    { q: "I'm a one-man operation — is this overkill?", a: "No — it's built for exactly that. When it's just you, you can't be on the tools and on the phone at once. The system answers, quotes, and books while you work, so a solo operator runs like a team. Start with Website + AI Chat and add the rest as you grow." },
    { q: "Do I need to be techy to run this?", a: "Not at all. We build it, set it up, and manage it for you. You get the jobs and the bookings; we handle the tech. If you can read a text message, you can run this." },
    { q: "Is there a minimum term?", a: FAQ_TERM },
    { q: "What if it's not the right fit?", a: FAQ_FIT },
    { q: "Can I start small and upgrade later?", a: "Yes. Start with Website + AI Chat and move up to the Full Pipeline whenever you're ready. Everything you build carries forward — no rework, no restart." },
  ],
  custom: {
    heading: "Need something bigger?",
    body: "Multiple crews, multiple locations, a franchise, or a trade we haven't built for yet? We'll scope it with you and quote it properly.",
  },
  customPackage: {
    number: "03",
    name: "Custom Systems",
    forWho: "For firms running several crews or branches.",
    tagline: "Your whole operation in one system.",
    description:
      "Several crews, branches or a franchise, or a trade we haven't built for yet? We'll scope it with you on one call and give you a fixed quote before any work starts.",
    badge: "Quoted by scope",
    floor: CUSTOM_FLOOR,
    note: "Monthly care plan priced alongside the build",
    features: [
      "Setups for several crews and branches",
      "Job, quote and invoice integrations",
      "Custom software for your trade",
      "A fixed quote after one scoping call",
      "A monthly care plan priced with the build",
    ],
    cta: "Book a Scoping Call",
    guarantee: GUARANTEE.custom,
  },
  guarantees: GUARANTEES,
  cta: {
    title: ["Ready to stop", "losing jobs?"],
    subhead: "Tell us your trade and the areas you cover. We'll come back with a clear plan — no jargon, no hard sell.",
    primary: "Get My System",
  },
}
